import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import { SessionProvider } from "./auth/SessionProvider";
import "bootstrap-icons/font/bootstrap-icons.css";
import { LanguageProvider } from "./i18n/LanguageProvider";
import { loadScript } from "./lib/loadScript";
import { initPageAnimations } from "./lib/animations";

// ──────────────────────────────────────────────────────────────
// Recover cleanly when a browser keeps old index.html and asks for
// a now-deleted Vite chunk after deployment.
// ──────────────────────────────────────────────────────────────
function installStaleChunkRecovery() {
  if (typeof window === "undefined") return;

  const shouldRecover = (value: unknown) => {
    const text =
      value instanceof Error
        ? value.message
        : typeof value === "string"
          ? value
          : String((value as { message?: unknown } | null)?.message || value || "");
    return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(text);
  };

  const recover = async (reason: unknown) => {
    if (!shouldRecover(reason)) return;
    const global = window as typeof window & { __ASSET_VERSION__?: string };
    const version = global.__ASSET_VERSION__ || "unknown";
    const key = `convobuddy:stale-chunk-reloaded:${version}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");

    try {
      if ("caches" in window) {
        const names = await window.caches.keys();
        await Promise.all(names.map((name) => window.caches.delete(name)));
      }
    } catch {
      // Best effort: a normal reload is still enough for non-service-worker caches.
    }

    window.location.reload();
  };

  window.addEventListener("error", (event) => {
    void recover(event.error || event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    void recover(event.reason);
  });
}

installStaleChunkRecovery();

// ──────────────────────────────────────────────────────────────
// Dev-only: patch React DevTools issues
// ──────────────────────────────────────────────────────────────
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
  if (hook && typeof hook.inject === "function") {
    hook.inject = function () {
      return {
        onCommitFiberRoot: () => {},
        onCommitFiberUnmount: () => {}
      };
    };
  }

  setTimeout(() => {
    if (!(window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
        inject: () => ({
          onCommitFiberRoot: () => {},
          onCommitFiberUnmount: () => {}
        }),
        supportsFiber: true
      };
    }
  }, 0);
}

// ──────────────────────────────────────────────────────────────
// Defer non-critical side effects to idle time
// ──────────────────────────────────────────────────────────────
async function loadRuntimeConfig() {
  if (typeof window === "undefined") return;
  const global = window as typeof window & {
    __PUBLIC_CONFIG__?: Record<string, unknown>;
    __CB_CONFIG__?: Record<string, unknown>;
  };

  if (global.__PUBLIC_CONFIG__ || global.__CB_CONFIG__) return;

  try {
    await loadScript("/config.js");
  } catch (error) {
    console.error("Unable to load runtime config", error);
  }
}

function runDeferredSideEffects() {
  if (typeof window === "undefined") return;

  const runIdleSideEffects = () => {
    import("./lib/logger")
      .then((m) => m.initClientLogger?.())
      .catch(console.error);
    initPageAnimations();
  };

  if (window.requestIdleCallback) {
    window.requestIdleCallback(runIdleSideEffects, { timeout: 2000 });
  } else {
    setTimeout(runIdleSideEffects, 200);
  }
}

// ──────────────────────────────────────────────────────────────
// Render app – App itself handles route-level Suspense,
// but we also wrap the whole tree to catch any lazy imports
// ──────────────────────────────────────────────────────────────
async function bootstrap() {
  await loadRuntimeConfig();
  await import("./i18n/setup");
  runDeferredSideEffects();

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <SessionProvider>
            <LanguageProvider>
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
                    Loading…
                  </div>
                }
              >
                <App />
              </Suspense>
            </LanguageProvider>
          </SessionProvider>
        </BrowserRouter>
      </HelmetProvider>
    </React.StrictMode>
  );
}

void bootstrap();
